import React, { useState, useEffect, useRef } from "react";
import {
  Paper,
  Text,
  Button,
  Stack,
  Table,
  Alert,
  Group,
  Box,
  TextInput,
  Badge,
  Loader,
  Progress,
  ActionIcon,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconLink,
  IconCheck,
  IconCopy,
  IconExternalLink,
} from "@tabler/icons-react";
import { BusinessInfo } from "../../../types/context";
import { useTemplate } from "../../../context/TemplateContext";
import { useSalon } from "../../../context/SalonContext";
import {
  DomainInfo,
  DeployStatusResponse,
} from "../../../types/staticWebGenerator";

// Color constants
const COLOR_BACKGROUND_LIGHT = "#f8f9fa";
const COLOR_BORDER_LIGHT = "#e9ecef";
const COLOR_ICON_GRAY = "#6c757d";

interface WebsiteURLCustomDomainProps {
  onDomainTypeChange?: (domainType: "built-in") => void;
  businessInfo?: BusinessInfo | null;
}

const WebsiteURLCustomDomain: React.FC<WebsiteURLCustomDomainProps> = ({
  onDomainTypeChange,
  businessInfo,
}) => {
  const salonId = businessInfo?.id || "";
  const { registerCustomDomain, getDeployStatus, listAllDomains } =
    useTemplate();

  // State
  const [domains, setDomains] = useState<DomainInfo[]>([]);
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
  const [deployStatus, setDeployStatus] = useState<DeployStatusResponse | null>(
    null,
  );
  const [newDomain, setNewDomain] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch domains on mount
  useEffect(() => {
    if (salonId) {
      fetchDomains();
    }
  }, [salonId]);

  // Start/stop polling based on status
  useEffect(() => {
    if (
      selectedDomain &&
      deployStatus?.status === "pending" &&
      !pollingIntervalRef.current
    ) {
      startPolling();
    } else if (
      deployStatus?.status === "active" ||
      deployStatus?.status === "failed"
    ) {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [selectedDomain, deployStatus?.status]);

  const fetchDomains = async () => {
    if (!salonId) return;

    try {
      setIsLoading(true);
      setError(null);
      const response = await listAllDomains(salonId);
      setDomains(response.domains || []);

      // Select the first domain if available
      if (response.domains && response.domains.length > 0) {
        const firstDomain = response.domains[0];
        setSelectedDomain(firstDomain.custom_domain);
        fetchDeployStatus(firstDomain.custom_domain);
      }
    } catch (err) {
      console.error("Error fetching domains:", err);
      setError("Failed to load domains. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDeployStatus = async (domain: string) => {
    if (!salonId || !domain) return;

    try {
      const status = await getDeployStatus(salonId, domain);
      setDeployStatus(status);
    } catch (err) {
      console.error("Error fetching deploy status:", err);
    }
  };

  const startPolling = () => {
    if (pollingIntervalRef.current) return;

    pollingIntervalRef.current = setInterval(() => {
      if (selectedDomain) {
        fetchDeployStatus(selectedDomain);
      }
    }, 30000); // Poll every 30 seconds
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const handleRegisterDomain = async () => {
    if (!newDomain.trim()) {
      setError("Please enter a domain name");
      return;
    }

    // Basic domain validation
    const domainRegex =
      /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*\.[a-z]{2,}$/i;
    if (!domainRegex.test(newDomain.trim())) {
      setError("Please enter a valid domain name (e.g., mysalon.com)");
      return;
    }

    if (!salonId) {
      setError("Salon ID is missing");
      return;
    }

    try {
      setIsRegistering(true);
      setError(null);
      setSuccess(null);

      const response = await registerCustomDomain(salonId, {
        custom_domain: newDomain.trim().toLowerCase(),
        domain_type: "production",
      });

      setNewDomain("");
      setSelectedDomain(response.custom_domain);
      setSuccess(
        `Domain registered! Setting up your domain... This may take 15-30 minutes.`,
      );

      // Refresh domains list
      await fetchDomains();

      // Start polling for status
      await fetchDeployStatus(response.custom_domain);
    } catch (err: any) {
      console.error("Error registering domain:", err);
      const errorMessage =
        err?.message || "Failed to register domain. Please try again.";
      setError(errorMessage);
    } finally {
      setIsRegistering(false);
    }
  };

  const handleDomainSelect = (domain: string) => {
    setSelectedDomain(domain);
    fetchDeployStatus(domain);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // Could add a toast notification here
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return <Badge color="green">Active</Badge>;
      case "pending":
        return <Badge color="yellow">Pending</Badge>;
      case "failed":
        return <Badge color="red">Failed</Badge>;
      default:
        return <Badge color="gray">Unknown</Badge>;
    }
  };

  return (
    <Paper p="md">
      <Text size="xl" fw={700} mb="md">
        Your URL
      </Text>

      <Text c="dimmed" mb="lg">
        Set and manage the web address where people can find your site.
      </Text>

      <Group gap="md" mb="lg">
        <Button
          variant="outline"
          color="gray"
          size="md"
          onClick={() => onDomainTypeChange?.("built-in")}
        >
          Built-In Domain
        </Button>
        <Button variant="filled" color="blue" size="md">
          Use a Custom Domain
        </Button>
      </Group>

      {isLoading ? (
        <Group justify="center" py="xl">
          <Loader size="sm" />
          <Text c="dimmed">Loading domains...</Text>
        </Group>
      ) : (
        <Stack gap="md">
          {/* Register New Domain */}
          <Paper p="md" style={{ border: `1px solid ${COLOR_BORDER_LIGHT}` }}>
            <Text fw={600} mb="sm">
              Register New Domain
            </Text>
            <Group>
              <TextInput
                placeholder="mysalon.com"
                value={newDomain}
                onChange={(e) => {
                  setNewDomain(e.target.value);
                  setError(null);
                }}
                style={{ flex: 1 }}
                disabled={isRegistering}
              />
              <Button
                onClick={handleRegisterDomain}
                loading={isRegistering}
                disabled={!newDomain.trim()}
              >
                Register Domain
              </Button>
            </Group>
            <Text size="xs" c="dimmed" mt="xs">
              Enter your domain name (e.g., mysalon.com)
            </Text>
          </Paper>

          {/* Existing Domains List */}
          {domains.length > 0 && (
            <Paper p="md" style={{ border: `1px solid ${COLOR_BORDER_LIGHT}` }}>
              <Text fw={600} mb="sm">
                Your Domains
              </Text>
              <Stack gap="xs">
                {domains.map((domain) => (
                  <Group
                    key={domain.custom_domain}
                    justify="space-between"
                    p="sm"
                    style={{
                      backgroundColor:
                        selectedDomain === domain.custom_domain
                          ? COLOR_BACKGROUND_LIGHT
                          : "transparent",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleDomainSelect(domain.custom_domain)}
                  >
                    <Group>
                      <Text fw={500}>{domain.custom_domain}</Text>
                      {getStatusBadge(domain.status)}
                    </Group>
                    {selectedDomain === domain.custom_domain && (
                      <IconCheck size={16} color="blue" />
                    )}
                  </Group>
                ))}
              </Stack>
            </Paper>
          )}

          {/* Deployment Status */}
          {selectedDomain && deployStatus && (
            <Paper p="md" style={{ border: `1px solid ${COLOR_BORDER_LIGHT}` }}>
              <Group justify="space-between" mb="md">
                <Text fw={600}>Deployment Status</Text>
                {getStatusBadge(deployStatus.status)}
              </Group>

              {deployStatus.status === "pending" && (
                <Stack gap="md">
                  <Alert icon={<IconAlertCircle size={16} />} color="yellow">
                    <Text fw={600} mb="xs">
                      Setting up your domain...
                    </Text>
                    <Text size="sm">
                      This process may take 15-30 minutes. Please add the DNS
                      records below to your domain provider (e.g., GoDaddy).
                    </Text>
                    {deployStatus.estimated_completion_time && (
                      <Text size="sm" mt="xs">
                        Estimated completion:{" "}
                        {new Date(
                          deployStatus.estimated_completion_time,
                        ).toLocaleString()}
                      </Text>
                    )}
                  </Alert>

                  {/* DNS Validation Records */}
                  {deployStatus.validation_records &&
                    deployStatus.validation_records.length > 0 && (
                      <Box>
                        <Text fw={600} mb="sm">
                          DNS Records to Add
                        </Text>
                        <Text size="sm" c="dimmed" mb="sm">
                          Go to your Domain Provider (e.g. GoDaddy) and add
                          these records to your DNS configuration:
                        </Text>
                        <Table>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>Type</Table.Th>
                              <Table.Th>Name</Table.Th>
                              <Table.Th>Value</Table.Th>
                              <Table.Th>Action</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {deployStatus.validation_records.map(
                              (record, index) => (
                                <Table.Tr key={index}>
                                  <Table.Td>{record.type}</Table.Td>
                                  <Table.Td>
                                    <Text
                                      size="sm"
                                      style={{ fontFamily: "monospace" }}
                                    >
                                      {record.name}
                                    </Text>
                                  </Table.Td>
                                  <Table.Td>
                                    <Group gap="xs">
                                      <Text
                                        size="sm"
                                        style={{
                                          fontFamily: "monospace",
                                          wordBreak: "break-all",
                                        }}
                                      >
                                        {record.value}
                                      </Text>
                                      <ActionIcon
                                        variant="subtle"
                                        size="sm"
                                        onClick={() =>
                                          copyToClipboard(record.value)
                                        }
                                        title="Copy to clipboard"
                                      >
                                        <IconCopy size={14} />
                                      </ActionIcon>
                                    </Group>
                                  </Table.Td>
                                  <Table.Td>
                                    <Button
                                      variant="subtle"
                                      size="xs"
                                      leftSection={<IconCopy size={14} />}
                                      onClick={() =>
                                        copyToClipboard(record.value)
                                      }
                                    >
                                      Copy
                                    </Button>
                                  </Table.Td>
                                </Table.Tr>
                              ),
                            )}
                          </Table.Tbody>
                        </Table>
                      </Box>
                    )}
                </Stack>
              )}

              {deployStatus.status === "active" && (
                <Stack gap="sm">
                  <Alert icon={<IconCheck size={16} />} color="green">
                    <Text fw={600} mb="xs">
                      Your domain is live!
                    </Text>
                    <Text size="sm">
                      Your website is now accessible at{" "}
                      <Text
                        component="a"
                        href={`https://${deployStatus.custom_domain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        c="blue"
                        td="underline"
                        fw={500}
                      >
                        {deployStatus.custom_domain}
                      </Text>
                    </Text>
                  </Alert>
                  <Group>
                    <Button
                      variant="outline"
                      leftSection={<IconExternalLink size={16} />}
                      component="a"
                      href={`https://${deployStatus.custom_domain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Visit Website
                    </Button>
                  </Group>
                </Stack>
              )}

              {deployStatus.status === "failed" && (
                <Alert icon={<IconAlertCircle size={16} />} color="red">
                  <Text fw={600} mb="xs">
                    Domain setup failed
                  </Text>
                  <Text size="sm">
                    {deployStatus.error_message ||
                      "An error occurred during domain setup. Please try again or contact support."}
                  </Text>
                </Alert>
              )}
            </Paper>
          )}

          {/* Error Message */}
          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red">
              {error}
            </Alert>
          )}

          {/* Success Message */}
          {success && (
            <Alert icon={<IconCheck size={16} />} color="green">
              {success}
            </Alert>
          )}

          {/* Warning about DNS configuration */}
          {deployStatus?.status === "pending" && (
            <Alert icon={<IconAlertCircle size={16} />} color="yellow">
              <Text fw={600}>Warning:</Text> Incorrectly configuring your DNS
              records can break existing functionality. If you're unsure of what
              to do, please reach out to our team at{" "}
              <Text
                component="a"
                href="mailto:contact@getpagoda.com"
                c="blue"
                td="underline"
              >
                contact@getpagoda.com
              </Text>
              .
            </Alert>
          )}

          {/* Support Section */}
          <Paper
            p="md"
            style={{
              backgroundColor: COLOR_BACKGROUND_LIGHT,
              border: `1px solid ${COLOR_BORDER_LIGHT}`,
              borderRadius: "8px",
            }}
          >
            <Group align="flex-start" gap="md">
              <IconLink size={24} color={COLOR_ICON_GRAY} />
              <Box>
                <Text size="lg" fw={600} mb="xs">
                  Need further support with your website URL?
                </Text>
                <Text c="dimmed">
                  Update your settings with your DNS provider, reach out to{" "}
                  <Text
                    component="a"
                    href="mailto:contact@getpagoda.com"
                    c="blue"
                    td="underline"
                  >
                    contact@getpagoda.com
                  </Text>{" "}
                  if you need assistance.
                </Text>
              </Box>
            </Group>
          </Paper>
        </Stack>
      )}
    </Paper>
  );
};

export default WebsiteURLCustomDomain;
